import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';

import { OrderTypeEnum } from '@flipster/apis/types';
import { FormattedMessage } from '@flipster/i18n';
import { spotOrdersBunja } from '@flipster/r230522/original/spot-order';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import Num from '@flipster/utils/num';
import segment, { ChartEvent } from '@flipster/utils/segment';

import { useTradingViewDataStorage } from '../../../misc/data-manager';
import { symbolNameToInfo } from '../../../misc/helper';
import { TvEventHandler } from '../../../trading-view';
import { useTvHeaderContext } from '../../index';
import SpotOrderLineHelper from './line-helpers/spot-order-line-helper';

export const SpotLimitOrdersPlugin = () => {
  const tv = useTvHeaderContext();

  const lineHelperRef = useRef<SpotOrderLineHelper>(null);

  const [checked, setChecked] = useTradingViewDataStorage({ key: 'spotLimitOrderVisible' });
  const [symbol, setSymbol] = useState<string>('');

  const { pendingPositionsAtom } = useBunja(spotOrdersBunja);
  const pendingPositions = useAtomValue(pendingPositionsAtom);

  useEffect(() => {
    if (!lineHelperRef.current)
      lineHelperRef.current = new SpotOrderLineHelper(tv, 'Limit', (order) => Num(order?.price || 0).toNumber());
    const handler: TvEventHandler<'onFirstDataLoaded'> = ({ symbol: newSymbol, symbolChanged }) => {
      if (!symbolChanged) return;
      lineHelperRef.current?.clean();
      setSymbol(newSymbol);
    };
    return tv.on('onFirstDataLoaded', handler);
  }, [tv, symbol]);

  const { symbol: symbolWithoutPriceType } = symbolNameToInfo(symbol);
  const symbolPositions = pendingPositions?.[symbolWithoutPriceType]?.filter(
    (position) => position.orderType === OrderTypeEnum['Limit'],
  );

  useEffect(() => {
    if (!checked.value || !symbolPositions?.length) {
      lineHelperRef.current?.clean();
      return;
    }
    lineHelperRef.current?.draw(symbol, symbolPositions);
  }, [checked, symbol, symbolPositions]);

  useEffect(() => {
    return () => {
      lineHelperRef.current?.clean();
    };
  }, []);

  return (
    <Checkbox
      checked={checked.value}
      onChange={(e) => {
        setChecked(e.currentTarget.checked);
        segment.track(ChartEvent.ChartEventToggleClick, {
          event_name: 'Trigger orders',
          action: e.currentTarget.checked ? 'turn_on' : 'turn_off',
        });
      }}
    >
      <FormattedMessage id='Trade.r260616.Limit_orders' />
    </Checkbox>
  );
};
